import React, { ReactElement, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from "react-router-dom";
import { useQuery } from 'react-query';

import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';

import DeleteIcon from '@material-ui/icons/Delete';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import ErrorIcon from '@material-ui/icons/Error';
// import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { green, orange } from '@material-ui/core/colors';
import { DateTime } from "luxon";

import { useAuthStore } from '../../AuthStore';

import {
  DataGrid,
  GridCellParams,
  GridSelectionModelChangeParams,
  GridColDef,
  GridPageChangeParams,
  GridSortModel,
  GridSortModelParams,
} from '@material-ui/data-grid';

import { useEnergyApi } from '../../_api/EnergyApiContext';
import RecRegistryBundleSnapshot, { RecRegistryBundleSnapshotRequest, RecRegistryBundleSnapshotStatus } from '../../_api/data/RecRegistryBundleSnapshot'
import { Paginated } from '../../_api/data/pagination'
import { stableValueHash } from 'react-query/types/core/utils';

function actionButtonsColumn(params: GridCellParams): ReactElement {
  const snapshot = params.row as RecRegistryBundleSnapshot
  const store = useAuthStore();

  const onDownload = () => {
    const link = document.createElement("a");
    // link.download = `download.txt`;
    link.href = snapshot.downloadLink;
    link.click();
  };

  return <div>
    {store.isAdmin &&
      <IconButton color="secondary" aria-label="delete>">
        <DeleteIcon />
      </IconButton>
    }

    {snapshot.status == RecRegistryBundleSnapshotStatus.created &&
      <IconButton color="primary" aria-label="download>" onClick={onDownload}>
        <CloudDownloadIcon />
      </IconButton>
    }
  </div>
}

function certificatesContainedColumn(params: GridCellParams): ReactElement {
  const snapshot = params.row as RecRegistryBundleSnapshot
  if (snapshot.containsSTCs && snapshot.containsLGCs) {
    return <Chip size="small" label="STCs & LGCs" color="primary" />
  }
  else if (snapshot.containsSTCs) {
    return <Chip size="small" label="STCs" />
  }
  else if (snapshot.containsLGCs) {
    return <Chip size="small" label="LGCs" />
  }
  return <Chip size="small" label="Empty" color="secondary" />
}

function luxonDateColumn(params: GridCellParams): ReactElement {
  const date = params.value as DateTime
  return <div>
    {date.toLocaleString(DateTime.DATE_MED)}
  </div>
}

function luxonDateTimeColumn(params: GridCellParams): ReactElement {
  const date = params.value as DateTime
  return <div>
    {date.toLocaleString(DateTime.DATETIME_MED)}
  </div>
}

const columns: GridColDef[] = [
  { field: 'date', headerName: 'Date', width: 400, renderCell: luxonDateColumn },
  { field: 'certificates', headerName: 'Certificates', width: 150, renderCell: certificatesContainedColumn },
  // { field: 'updatedAt', headerName: 'Updated At', width: 250, renderCell: luxonDateTimeColumn },
  { field: 'status', headerName: 'Status', width: 150 },
  { field: '', headerName: 'Actions', width: 250, renderCell: actionButtonsColumn },
];

type Props = {
}

const RecRegistryBundleSnapshotDataTable: React.FC<Props> = (): React.ReactElement => {
  const defaultRequest = (): RecRegistryBundleSnapshotRequest => {
    const request = new RecRegistryBundleSnapshotRequest()
    return request
  }

  useEffect(() => {
    const newRequest = RecRegistryBundleSnapshotRequest.copy(request);
    setRequest(newRequest)
  }, []);

  const history = useHistory()
  const energyApi = useEnergyApi()
  const [request, setRequest] = React.useState(defaultRequest())

  const queryGenerations = (): Promise<Paginated<RecRegistryBundleSnapshot>> => {
    return energyApi.getRecRegistryBundleSnapshots(request)
  }

  const handlePageChange = (params: GridPageChangeParams) => {
    const newRequest = RecRegistryBundleSnapshotRequest.copy(request)
    newRequest.page = params.page
    newRequest.per = params.pageSize
    setRequest(newRequest);
  };

  const setSortModel = (model: GridSortModel) => {
    const newRequest = RecRegistryBundleSnapshotRequest.copy(request)
    newRequest.sort = model
    setRequest(newRequest)
  };

  const handleSortModelChange = (params: GridSortModelParams) => {
    if (params.sortModel !== request.sort) {
      setSortModel(params.sortModel);
    }
  };

  // Queries
  const {
    isLoading,
    isError,
    error,
    data,
    isFetching,
    isPreviousData,
  } = useQuery<Paginated<RecRegistryBundleSnapshot>, Error>(['RecRegistryBundleSnapshots', request], (queryGenerations), { keepPreviousData: true });

  if (isLoading) {
    return <span>Loading...</span>
  }

  if (isError) {
    return <span>Error...</span>
  }

  if (data) {
    return (
      <div style={{ width: '100%' }}>
        <DataGrid
          autoHeight={true}
          rows={data.items}
          columns={columns}
          pagination
          pageSize={request.per}
          onPageSizeChange={handlePageChange}
          page={data.metadata.page}
          rowCount={data.metadata.total}
          paginationMode="server"
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          onPageChange={handlePageChange}
          loading={isLoading}
          disableColumnFilter
          disableSelectionOnClick
        />
      </div>
    )
  }

  return <span>Error</span>
};

RecRegistryBundleSnapshotDataTable.propTypes = {
  accreditationCodeId: PropTypes.string,
  generationYear: PropTypes.number,
  registeredPersonNumber: PropTypes.number,
  containsErrors: PropTypes.bool,
}

export default RecRegistryBundleSnapshotDataTable;
